import { useCallback, useEffect, useMemo, useState } from "react";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import {
  isIosStandaloneCapableUserAgent,
  isWithinQuietPeriod,
  nextVisitCount,
} from "../utils/installPromptRules";

/** Clave del rechazo persistente (guarda la fecha en milisegundos). */
const DISMISSED_KEY = "agricos:pwa-install-dismissed-at";
/** Marca de "ya lo cerré en esta pestaña": no vuelve a salir hasta recargar. */
const SESSION_KEY = "agricos:pwa-install-hidden";
/** Cuenta de aperturas: en iPhone el aviso sólo sale a partir de la segunda. */
const VISITS_KEY = "agricos:pwa-visits";

/** Espera antes de aparecer: primero que la persona use la app. */
const APPEAR_DELAY_MS = 25_000;
/** Visitas mínimas antes de sugerir el gesto manual de iOS. */
const IOS_MIN_VISITS = 2;

function safeRead(storage: Storage | undefined, key: string) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null; // Safari en privado lanza al tocar el almacenamiento
  }
}

function safeWrite(storage: Storage | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    /* sin almacenamiento el aviso simplemente vuelve a salir otro día */
  }
}

/** Cuenta cuántas veces se ha abierto la app en este navegador. */
function bumpVisits() {
  const next = nextVisitCount(safeRead(window.localStorage, VISITS_KEY));
  safeWrite(window.localStorage, VISITS_KEY, String(next));
  return next;
}

type PwaInstallVariant = "prompt" | "ios";

/**
 * Decide si toca ofrecer la instalación y con qué gesto.
 *
 * Reglas de "no molestar":
 * - Nunca si la app ya corre instalada (display-mode: standalone).
 * - Nunca dos veces en la misma sesión después de cerrarlo.
 * - Nada durante los primeros segundos de uso ni si se rechazó hace poco.
 */
export function usePwaInstallPrompt() {
  const install = usePwaInstall();
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [visits, setVisits] = useState(0);

  useEffect(() => {
    setVisits(bumpVisits());
  }, []);

  const variant: PwaInstallVariant | null = useMemo(() => {
    if (install.installed) return null;
    if (install.canInstall) return "prompt";
    if (install.isIos && isIosStandaloneCapableUserAgent(navigator.userAgent) && visits >= IOS_MIN_VISITS) {
      return "ios";
    }
    return null;
  }, [install.canInstall, install.installed, install.isIos, visits]);

  useEffect(() => {
    if (!variant) {
      setVisible(false);
      return;
    }
    if (safeRead(window.sessionStorage, SESSION_KEY) === "1") return;
    if (isWithinQuietPeriod(safeRead(window.localStorage, DISMISSED_KEY))) return;

    const timer = window.setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [variant]);

  /** Cerrar a mano: fuera en esta sesión y en silencio un mes. */
  const dismiss = useCallback(() => {
    setVisible(false);
    safeWrite(window.sessionStorage, SESSION_KEY, "1");
    safeWrite(window.localStorage, DISMISSED_KEY, String(Date.now()));
  }, []);

  /** "Ahora no" del diálogo nativo: fuera de esta sesión, sin castigo de un mes. */
  const snooze = useCallback(() => {
    setVisible(false);
    safeWrite(window.sessionStorage, SESSION_KEY, "1");
  }, []);

  const accept = useCallback(async () => {
    setInstalling(true);
    try {
      const accepted = await install.install();
      if (accepted) {
        setVisible(false);
        safeWrite(window.sessionStorage, SESSION_KEY, "1");
      } else {
        snooze();
      }
      return accepted;
    } finally {
      setInstalling(false);
    }
  }, [install, snooze]);

  return { visible, variant, installing, accept, dismiss };
}
